import { forwardRef } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import MuiPaper, { type PaperProps } from '@mui/material/Paper';
import type { SxProps, Theme } from '@mui/material/styles';
import Box from '../primitives/Box';
import { form } from '@/shared/theme/tokens';
import { mergeSx } from '../utils/mergeSx';

/** Paper flex column: only the menu list scrolls; create action stays pinned at the bottom. */
const stickyCreateFooterPaperSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '& > .MuiList-root, & > .MuiAutocomplete-listbox': {
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'auto',
  },
};

export type QuickCreateMenuPaperProps = PaperProps & {
  showCreate?: boolean;
  createLabel?: string;
  onCreate?: () => void;
};

/**
 * Menu Paper (Select / Autocomplete) with a fixed create action below the scrolling list.
 * Footer sits outside the listbox, so it never scrolls away.
 * `onMouseDown` preventDefault keeps Autocomplete/Select from closing before click.
 */
const QuickCreateMenuPaper = forwardRef<HTMLDivElement, QuickCreateMenuPaperProps>(
  function QuickCreateMenuPaper(
    { children, showCreate = false, createLabel, onCreate, sx, ...other },
    ref
  ) {
    const showFooter = showCreate && Boolean(createLabel) && Boolean(onCreate);

    return (
      <MuiPaper
        {...other}
        ref={ref}
        sx={mergeSx(showFooter ? stickyCreateFooterPaperSx : undefined, sx)}
      >
        {children}
        {showFooter ? (
          <Box
            component="button"
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onCreate}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              flexShrink: 0,
              border: 0,
              borderTop: 1,
              borderColor: 'divider',
              borderRadius: 0,
              m: 0,
              px: form.menuItemPaddingX,
              py: form.menuItemPaddingY,
              minHeight: form.menuItemMinHeight,
              bgcolor: 'background.paper',
              color: 'primary.main',
              font: 'inherit',
              fontSize: form.fontSize,
              fontWeight: 600,
              textAlign: 'left',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <AddIcon fontSize="small" />
            {createLabel}
          </Box>
        ) : null}
      </MuiPaper>
    );
  }
);

export default QuickCreateMenuPaper;
